// Single source for the glow-category constants and suggestion logic shared by
// the create page (GlowCreate), the settings tab (SettingsTab), and the
// "Your glows" list grouping + bulk-move dialog (GlowList). `category` is a
// free, NOT subscription-gated field — a structural clone of `title`. Keeping
// the default list and the length cap here (rather than hardcoded at each call
// site) means a tweak to either lands once.

// DEFAULT_CATEGORIES seeds the combobox suggestions only — these never force an
// empty group onto the list page. Exact order + casing are locked. `as const`
// makes the tuple read-only and literally typed (mirrors lib/plans.ts).
export const DEFAULT_CATEGORIES = [
  'Work',
  'Personal',
  'Family',
  'Clients',
  'Side Projects',
  'Demos',
  'Experiments',
] as const

// MAX_CATEGORY_LENGTH single-sources the client-side cap to the server const
// `glows.MaxCategoryLength = 50` (internal/glows/validate.go). The combobox
// enforces it via the input `maxLength` attribute; the server is the hard
// boundary (returns `invalid_category` 400 on overflow).
export const MAX_CATEGORY_LENGTH = 50

// mergeCategorySuggestions unions DEFAULT_CATEGORIES + the owner's in-use
// categories (`existing`, in caller order) + the value currently being edited
// (`current`, so a brand-new typed value still shows as a selectable
// suggestion), de-duped CASE-INSENSITIVELY keeping the first-seen casing, and
// ordered defaults-first then the rest in first-seen order. Pure — no I/O, no
// React, and it mutates neither argument. Callers pass the result straight to
// CategoryCombobox's `suggestions` prop (the combobox never calls this itself).
export function mergeCategorySuggestions(
  existing: string[],
  current?: string,
): string[] {
  const trimmedCurrent = current?.trim() ?? ''
  const seed: string[] = [
    ...DEFAULT_CATEGORIES,
    ...existing,
    ...(trimmedCurrent !== '' ? [trimmedCurrent] : []),
  ]
  const seen = new Set<string>()
  const result: string[] = []
  for (const candidate of seed) {
    // Defensive: a caller may slip an undefined/empty in (e.g. a glow from an
    // older server with no `category`). Skip it rather than throw on
    // .toLowerCase() — the type guard at call sites is the first line, this is
    // belt-and-suspenders so this shared primitive can never crash the page.
    if (!candidate) continue
    const key = candidate.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push(candidate)
  }
  return result
}
